import { DEVICE_STORAGE_KEYS } from "../constants/storage";

// Privacy Mode ("Hide sensitive data").
//
// Masks commercially sensitive values — prices, totals, partner and document names, comment
// bodies — so a user can keep working while somebody else can see the screen: a customer visit,
// a screen share, an open-plan desk, a train. Quantities are deliberately not part of the set.
//
// IMPORTANT — what this is and is not:
// This is a DISPLAY feature, not an access control. The masking happens at render time in the
// browser; the real values are still in the GraphQL response. That is fine for the threat it
// addresses (a bystander reading the screen), because the person switching it on is the
// logged-in user, who already has permission to see this data. Do NOT use it to withhold data
// from a user who must not have it — that belongs in the permission system (see
// src/helpers/permission/) or in real server-side field withholding.
//
// Stored in two places, on purpose:
//
//   the PREFERENCES  — which classes and which treatment — live in the free-form
//   `Users.settings` AWSJSON blob under `hideData`, exactly like `cookieConsent`,
//   `defaultFilters` and `quickFilters`. No schema change, and they sync across devices via
//   refreshUserPermissions() in src/App.js.
//
//   the SWITCH — whether the mode is on right now — lives on the DEVICE, see
//   readEnabled/writeEnabled at the bottom of this file. It is a situation, not a preference,
//   and keeping it out of the shared blob is what makes toggling instant and unclobberable.
//
// Configured on the Privacy Mode page (src/pages/privacyMode/PrivacyMode.js).
//
// ── If you just need to hide a value, you need four things ───────────────────────────────────
//
// Money             nothing. currencyFormatter / priceFormatter / percantageFormatter in
//                   helpers/Formatters.js mask themselves. Need your own options? Use
//                   createNumberFormatter(options, dataClass) — never `new Intl.NumberFormat`
//                   for money (an ESLint rule enforces this).
// A value in JSX    <HiddenValue dataClass="partners"> from components/HiddenValue.js.
// A table column    `sensitive: "partners"` on the column (StripedRowsTable), or on a row of a
//                   BusinessCard.
// A title / alt     titleIfVisible(value, dataClass) — below.
//
// Two more for the awkward cases:
//
// A string context  maskIfHidden(value, dataClass) — a prop typed as a string, a chart label,
//                   an HTML string handed to d3. Plus useHideData() in that component, or it
//                   will not redraw when the mode is toggled.
// A tooltip         pass `tooltip` to <HiddenValue> so it follows the value's reveal state.
//                   tooltipTrigger() only for a tooltip with no single value behind it.
//
// Everything else exported here — DATA_CLASSES, TREATMENTS, getHideDataConfig,
// setHideDataConfig, isClassMasked, maskText, maskNumberString, maskForClass — is infrastructure
// for HiddenValue, Formatters and the provider. A screen should not need them; if you reach for
// one, one of the six above probably fits better. (Four call sites once hand-rolled their own
// copy of maskIfHidden before it existed, which is what this list is meant to prevent.)
//
// The full decision table, including what is deliberately NOT masked, is in CLAUDE.md.

// The mask character. A bullet, deliberately NOT a random fake digit: a crisp but wrong number
// is more dangerous than an obviously masked one, because anywhere we have not wrapped the
// value in a treatment a user could read the decoy as real. Bullets keep the character count
// (so nothing reflows) and are unmistakable.
const MASK_CHAR = "•";

// The data classes a user can switch on and off individually.
//
// `numeric` decides how a value of this class is masked: digits only (keeping thousand
// separators and the currency symbol, which is what a right-aligned money column needs) or
// every non-space character.
//
// `exampleFields` is shown under the label on the Privacy Mode page. It is intentionally the
// real field names users see in the app, in the language they see them in — do not translate.
export const DATA_CLASSES = [
  {
    key: "prices",
    label: "Prices and margins",
    exampleFields: "Preis, Margin, Sales Price",
    numeric: true,
  },
  {
    key: "orderValues",
    label: "Order values and totals",
    exampleFields: "Gesamt, Order Value",
    numeric: true,
  },
  {
    // Everything that identifies a business partner, not just the name: the address, the
    // contact details, the customer and VAT numbers, the ABC rating, the country an item is
    // sourced from. It started as names only, on the reasoning that a street is not a secret —
    // but a screen showing "•••••••" over a full address, phone number and customer number is
    // still a screen someone can write the partner down from, and splitting those into their
    // own switches only made the settings page longer without making a real decision easier.
    // One switch, one meaning: who we deal with, and how to reach them.
    key: "partners",
    label: "Partner names, addresses and numbers",
    exampleFields: "Name, address, e-mail, phone, customer no.",
    numeric: false,
  },
  {
    key: "documents",
    label: "Documents and attachments",
    exampleFields: "Quality Reports, Invoices",
    numeric: false,
  },
  {
    // Free text, so it can contain anything — prices, customer names, agreed terms. That is
    // why it gets its own class instead of riding along with one of the others.
    key: "comments",
    label: "Comments and notes",
    exampleFields: "Kommentare, Notizen",
    numeric: false,
  },
];

const CLASS_BY_KEY = DATA_CLASSES.reduce((acc, c) => {
  acc[c.key] = c;
  return acc;
}, {});

export const TREATMENTS = ["blur", "dots", "bars"];

// Defaults: prices, order values, partner names and comments are masked; document names are
// not — a file name is rarely the secret, and masking them made document lists hard to work
// with.
//
// ⚠️ The coverage audit found a side effect of this default: `documents` is the only class that
// is off by default AND was by far the worst covered — 7 of ~12 real gaps were file names. That
// is a feedback loop, not a coincidence: what is off by default is not missed when it is absent.
// While the default stands, `npm run privacy:audit` is the only check for this class; everyday
// use corrects the other four.
//
// getHideDataConfig below drops stored keys for classes that no longer exist (`payments` and
// `quantities` both had one), so an old config cannot resurrect a removed class.
//
// `enabled` is part of this shape but NOT part of what is stored in settings — it lives on the
// device, see readEnabled/writeEnabled below. The provider composes the two.
export const DEFAULT_HIDE_DATA_CONFIG = {
  enabled: false,
  classes: {
    prices: true,
    orderValues: true,
    partners: true,
    documents: false,
    comments: true,
  },
  treatment: "blur",
  lastChanged: null,
};

// ── The store ────────────────────────────────────────────────────────────────────────────────
//
// ONE place holds the resolved config, and both worlds read it: the React components through
// useHideData() (which subscribes via useSyncExternalStore), and the code that cannot call a
// hook — the `…Text()` formatters, the imperative d3 / Plotly / funnel-graph drawing — through
// isClassMasked() and friends directly.
//
// It used to be two places: this module held a MIRROR that HideDataProvider wrote to during its
// render phase, while the provider's own useState held the truth. Two consequences, both fixed
// by the store:
//
//   - `isMasked` existed twice, here and in useHideData.js, reading two different sources. The
//     same three lines in two files, deciding the single most important question in the
//     feature. Drift between them would have masked the JSX path and not the string path,
//     silently.
//   - Writing a module variable DURING render is a side effect in a function React may call
//     more than once and may throw away. It worked, but it is the pattern that breaks under
//     StrictMode's double render and concurrent rendering.
//
// The state is initialised lazily rather than at module load, so readEnabled() (defined further
// down) is not called inside the module's own evaluation.
let state = null;

const listeners = new Set();

// useSyncExternalStore compares snapshots by IDENTITY and re-renders in a loop if a new object
// comes back every time. `state` is therefore replaced only when something actually changed —
// see the guards in setEnabled and setPreferences.
export const getSnapshot = () => {
  if (state === null) {
    state = { ...DEFAULT_HIDE_DATA_CONFIG, enabled: readEnabled() };
  }
  return state;
};

export const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// Copy the set first: a listener that unsubscribes while being notified would otherwise
// mutate the collection being iterated.
const emit = () => [...listeners].forEach((listener) => listener());

// The switch. Device-local, instant, no database write and nothing in Redux — see the note on
// readEnabled/writeEnabled below for why it lives apart from the preferences.
export const setEnabled = (value) => {
  const enabled = !!value;
  if (getSnapshot().enabled === enabled) return;
  writeEnabled(enabled);
  state = { ...state, enabled };
  emit();
};

// The preferences: classes and treatment. `enabled` is carried over from the current
// state on purpose — it does not come from settings, and taking it from a hydrated preferences
// object switched the mode off every time the settings page saved.
export const setPreferences = (preferences) => {
  const current = getSnapshot();
  const next = { ...current, ...preferences, enabled: current.enabled };
  // Hydration runs on every change to the user object, most of which do not touch us. Without
  // this guard each one would hand useSyncExternalStore a fresh object and re-render every
  // masked value in the app.
  if (JSON.stringify(next) === JSON.stringify(current)) return;
  state = next;
  emit();
};

// Replaces the whole snapshot. Only for tests and for the settings page's optimistic update,
// where both halves are known — everything else uses setEnabled or setPreferences.
export const setHideDataConfig = (config) => {
  const next = config || DEFAULT_HIDE_DATA_CONFIG;
  if (JSON.stringify(next) === JSON.stringify(getSnapshot())) return;
  state = next;
  emit();
};

// Is this data class currently being masked? The single question every masking point asks —
// and, since the store, the single implementation of it.
//
// An unknown class name (or `true`, the Stage 1 shape) means "mask whenever the mode is on".
export const isClassMasked = (dataClass) => {
  const config = getSnapshot();
  if (!config.enabled) return false;
  if (!dataClass || dataClass === true) return true;
  return !!config.classes?.[dataClass];
};

// Internal: the only caller is maskForClass below. Not exported, so a masking point cannot get
// the idea of branching on it itself — the class already decides.
const isNumericClass = (dataClass) => !!CLASS_BY_KEY[dataClass]?.numeric;

// Masking the visible text is worthless if a tooltip next to it still spells the value out —
// hovering would hand a bystander exactly what we just hid. These two helpers suppress the
// tooltip instead of masking it: a tooltip full of bullets helps nobody.
//
//   title={titleIfVisible(company.name, "partners")}
//   <OverlayTrigger trigger={tooltipTrigger("partners")} overlay={…}>
//
// Use them for `title`, `aria-label` and `alt` — anything that surfaces the raw value outside
// the masked element. Note they read the module config, so they do NOT re-render on their own;
// they are for attributes on elements that re-render for other reasons. Where the tooltip has
// to follow a single value's reveal state, pass `tooltip` to <HiddenValue> instead.
export const titleIfVisible = (value, dataClass) =>
  isClassMasked(dataClass) ? undefined : value;

// react-bootstrap treats an empty trigger array as "never open".
export const tooltipTrigger = (dataClass, trigger = ["hover", "focus"]) =>
  isClassMasked(dataClass) ? [] : trigger;

// Reads the PREFERENCES off the user object and fills in the defaults: which classes, which
// treatment. Not the on/off switch — that lives on the device (readEnabled below),
// so `enabled` always comes back as the default here and the provider fills it in.
//
// A stored `enabled` from an earlier version is ignored rather than honoured, the same way a
// stored `payments` class is.
//
// Three pieces of defensiveness, all load-bearing:
//   - `settings` is normally a parsed object in Redux, but CookieBanner.js used to dispatch the
//     JSON *string* form, and sessions from before that fix still carry it.
//   - `hideData` was a bare boolean in the very first version; a stored boolean must not crash.
//   - `classes` is merged one level deep, so a config saved before a new data class existed
//     still gets that class's default rather than `undefined`.
export const getHideDataConfig = (user) => {
  let settings = {};
  try {
    settings =
      typeof user?.settings === "string"
        ? JSON.parse(user.settings)
        : user?.settings || {};
  } catch (e) {
    settings = {};
  }

  const raw = settings.hideData;
  // The very first version stored a bare boolean. Nothing to take from it now that the switch is
  // device-local, but it must not be spread as if it were a config object.
  const storedRaw = typeof raw === "object" && raw !== null ? raw : {};

  // `autoIdle` drove the automatic idle rule, which was dropped on feedback. Destructured out
  // rather than ignored, so a config saved while it existed does not carry it back into the
  // snapshot — and from there into every future save.
  // eslint-disable-next-line no-unused-vars
  const { autoIdle, ...stored } = storedRaw;

  // Whitelist against the classes that still exist. A config saved while the removed `payments`
  // or `quantities` classes were around would otherwise carry those keys forever, and
  // isClassMasked would honour them.
  const classes = { ...DEFAULT_HIDE_DATA_CONFIG.classes };
  Object.keys(classes).forEach((key) => {
    if (stored.classes && key in stored.classes) classes[key] = !!stored.classes[key];
  });

  return {
    ...DEFAULT_HIDE_DATA_CONFIG,
    ...stored,
    // Never taken from settings — the device decides. Spelled out after the spread so a stored
    // leftover cannot leak back in.
    enabled: DEFAULT_HIDE_DATA_CONFIG.enabled,
    classes,
    treatment: TREATMENTS.includes(stored.treatment)
      ? stored.treatment
      : DEFAULT_HIDE_DATA_CONFIG.treatment,
  };
};

// The on/off switch, stored per DEVICE rather than per account.
//
// Why not in `user.settings.hideData` with the rest: it is written on every Shift + H, and
// `settings` is one blob that ~20 writers rewrite wholesale. A frequently written value in there
// is what forced the provider to mirror state into Redux by hand and made this feature's
// persistence far more complicated than the feature itself. Out here, nothing can overwrite it.
//
// It also matches what the value means: whether somebody is looking at this screen is a property
// of the situation at this machine, not of the account. It therefore survives logging out, like
// the cookie consent next to it in DEVICE_STORAGE_KEYS.
//
// Both wrapped in try/catch: a private window or "block site data" makes localStorage throw on
// access, and Privacy Mode failing loudly there would be worse than it forgetting the switch.
export const readEnabled = () => {
  try {
    return localStorage.getItem(DEVICE_STORAGE_KEYS.PRIVACY_MODE) === "true";
  } catch (e) {
    return false;
  }
};

export const writeEnabled = (enabled) => {
  try {
    localStorage.setItem(
      DEVICE_STORAGE_KEYS.PRIVACY_MODE,
      enabled ? "true" : "false",
    );
  } catch (e) {
    // Nothing to do — the mode still works for this session, it just will not survive a reload.
  }
};

// Masks a formatted NUMBER string: every digit becomes a bullet, everything else — thousand
// separators, decimal comma, currency symbol, sign, percent, whitespace — is kept, so the
// string keeps its exact shape and length.
//
//   "1.234,56 €"  ->  "•.•••,•• €"
//   "-12,50 %"    ->  "-••,•• %"
export const maskNumberString = (value) => {
  if (value === null || value === undefined) return value;
  return String(value).replace(/[0-9]/g, MASK_CHAR);
};

// Masks free TEXT — a company name, a person, a reference number. Every non-space character
// becomes a bullet, so word count and word lengths survive and the layout does not shift.
//
//   "Test Supplier GmbH"  ->  "•••• •••••••• ••••"
export const maskText = (value) => {
  if (value === null || value === undefined) return value;
  return String(value).replace(/\S/g, MASK_CHAR);
};

// Masks a value the way its data class wants to be masked.
export const maskForClass = (value, dataClass) =>
  isNumericClass(dataClass) ? maskNumberString(value) : maskText(value);

// maskForClass, but only when the class is actually being masked — returns the value untouched
// otherwise. This is the string-context counterpart to <HiddenValue>, for the places that cannot
// take an element: a prop typed as a string (InfoTip runs its text through t()), an HTML string
// handed to a charting library, a d3 tooltip.
//
// Same caveat as titleIfVisible: it reads the module config, so it does NOT re-render on its own.
// In a component that has no other reason to re-render — a chart, anything drawing imperatively —
// call useHideData() as well and put the masking state in the dependencies. See
// SupplierPriceChart.js and CustomD3WorldMap.js.
export const maskIfHidden = (value, dataClass) =>
  isClassMasked(dataClass) ? maskForClass(value, dataClass) : value;
